<template>
  <div class="pdf-navigator" :class="{ 'pdf-navigator--bare': tab !== null }">
    <div v-if="tab === null" class="pdf-navigator__header">
      <v-tabs v-model="tabs" class="pdf-navigator__tabs" grow>
        <v-tab :ripple="tabs !== 0">
          <div class="pdf-navigator__tab-content">
            {{ $t('page.analysis.pdf-navigator.summary') }}
            <BaseTag tone="ai" preset="soft" icon="ai">
              {{ $t('tags.ai') }}
            </BaseTag>
          </div>
        </v-tab>
        <v-tab class="pdf-navigator__tab" :ripple="tabs !== 1">
          <div class="pdf-navigator__tab-content">
            {{ $t('page.analysis.pdf-navigator.keywords') }}
          </div>
        </v-tab>
        <v-tab
          class="pdf-navigator__tab"
          :class="{ 'disabled-tab': !tenantHasTasksAccess() }"
          :ripple="tabs !== 2 && tenantHasTasksAccess()"
          v-tooltip="tasksTooltipConfig"
        >
          <div class="pdf-navigator__tab-content">
            <BaseIcon v-if="!tenantHasTasksAccess()" name="lock" class="pdf-navigator__locked-module-icon" />
            {{ $t('page.analysis.pdf-navigator.tasks') }}
            <BaseTag tone="ai" preset="soft" icon="ai">
              {{ $t('tags.ai') }}
            </BaseTag>
          </div>
        </v-tab>
      </v-tabs>
    </div>
    <div class="pdf-navigator__content">
      <EvaluationGrid v-if="tabs === 0" :opportunityId="opportunityId" />
      <KeywordNavigation v-else-if="tabs === 1" />
      <TasksCard
        v-else-if="tabs === 2"
        minified
        :tasks="tasks"
        @task-click="onTaskClick"
        @task-assign="onTaskAssign"
        @task-create="onCreateTask"
        @tasks-import="onImportTasks"
      />
    </div>
  </div>
</template>

<script>
import BaseTag from '@/components/base/BaseTag.vue';
import EvaluationGrid from '@/components/page-pdf-analysis/EvaluationGrid';
import KeywordNavigation from '@/components/pdf-viewer/KeywordNavigation.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import TasksCard from '@/components/page-opportunity-tasks/TasksCard.vue';

import { tenantHasLLMExtractionsAccess, tenantHasTasksAccess } from '@/util/module-access-util';
import { mapGetters } from 'vuex';

export default {
  name: 'PDFNavigator',
  components: {
    BaseTag,
    BaseIcon,
    EvaluationGrid,
    KeywordNavigation,
    TasksCard,
  },
  props: {
    opportunityId: {
      type: String,
      required: true,
    },
    // The tab shown, set by a page that draws the tabs itself (the analysis page's bottom tabs on
    // phones and tablets); null, it draws its own.
    tab: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      tabs: this.tab === null ? 0 : this.tab,
    };
  },
  watch: {
    tab(value) {
      if (value !== null) this.tabs = value;
    },
  },
  computed: {
    ...mapGetters('documentKeywords', ['keywords']),
    ...mapGetters('tasks', ['tasks']),
    tasksTooltipConfig() {
      return !this.tenantHasTasksAccess() ? { content: this.$t('page.opportunity.access.tasks') } : null;
    },
  },
  methods: {
    tenantHasLLMExtractionsAccess,
    tenantHasTasksAccess,

    onTaskClick(task) {
      this.$emit('task-click', task);
    },
    onTaskAssign(payload) {
      this.$emit('task-assign', payload);
    },
    onCreateTask() {
      this.$emit('task-create');
    },
    onImportTasks() {
      this.$emit('tasks-import');
    },
  },
};
</script>

<style lang="scss">
.pdf-navigator {
  height: 100%;
  width: 100%;
  padding: 1rem;
  background-color: $color-white;
  border-right: 1px solid $color-border;

  &__header {
    background-color: $color-white;
    margin-bottom: 1rem;
    display: flex;

    .base-btns-toggle {
      max-height: min-content;
    }
  }

  .v-tab {
    color: $color-neutral !important;

    &::before {
      background: $color-normal;
    }
  }

  .v-tab--active {
    color: $color-normal !important;
    background: transparent;

    .pdf-navigator__settings-icon {
      transition: color 200ms ease;

      &:hover {
        color: $color-accent;
        transition: color 200ms ease;
      }
    }

    &::before {
      background: transparent;
    }
  }

  &__tab-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  &__locked-module-icon {
    width: 14px;
    height: 14px;
    margin-right: 8px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    height: calc(100% - 56px);
    position: relative;
    overflow-y: auto;

    &::-webkit-scrollbar-track {
      background-color: $color-neutral-pale;
    }

    &::-webkit-scrollbar {
      height: 0;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-large;
      background-color: $color-grey-pale;
    }

    .base-select {
      flex: 0 1 auto;
    }

    .tasks-card {
      box-shadow: unset;
      padding: 0;
    }
  }

  &__tag {
    margin-left: 4px;
  }

  &--bare &__content {
    height: 100%;
  }

  .disabled-tab {
    opacity: 0.5;

    &:hover {
      pointer-events: auto;
    }

    &:active {
      pointer-events: none;
    }
  }

  // Its tabs fill the panel, a few pixels too wide for it: Vuetify neither slides them nor shows
  // its arrows.
  .v-slide-group__content.v-tabs-bar__content {
    transform: translateX(0px) !important;
  }

  .v-slide-group.v-item-group > .v-slide-group__next,
  .v-slide-group.v-item-group > .v-slide-group__prev {
    display: none;
  }
}
</style>
